import { hierarchy, stratify } from "d3-hierarchy";
import { ArtworkNode, IArtworkSprite, SetData } from "../../interfaces";
import _ from "lodash";
import {
  forceCollide,
  forceLink,
  forceManyBody,
  forceRadial,
  forceSimulation,
} from "d3-force";

import { forceManyBodyReuse } from "d3-force-reuse";
import { Simulation } from "..";

export class ClusterSimulation extends Simulation {
  private stratifiedData: any;
  private readonly centeredArtwork: any;
  private readonly artworks: any[];
  private readonly artworkSprites: IArtworkSprite[];
  public root: any;
  public links: any;
  public nodes: any;
  public centerNode: any;
  public dimensions: { width: number; height: number };
  public onSimulationEnd?: (root: any, links: any, nodes: any) => void;
  private readonly nodePadding = 20;
  private simulationResults: {
    nodes: any[];
    links: any[];
    root: any;
  } | null = null;

  constructor(
    centeredArtwork: any,
    artworks: any[],
    artworkSprites: IArtworkSprite[],
    dimensions: any,
  ) {
    super();
    this.centeredArtwork = centeredArtwork;
    this.artworks = artworks;
    this.artworkSprites = artworkSprites;
    this.dimensions = dimensions;
    this.stratifiedData = stratify();
    this.setup();
  }

  protected setup(): void {
    this.stratifiedData
      .id((d: ArtworkNode) => d.uuid)
      .parentId((d: ArtworkNode) => d.parentId);

    const hierarchyData = this.generateParentNodes();

    this.root = hierarchy(this.stratifiedData(hierarchyData));
    this.links = this.root.links();
    this.nodes = this.root.descendants();

    this.centerNode = this.nodes.find(
      (node: any) => node.data.data.uuid === this.centeredArtwork.uuid,
    ) as d3.SimulationNodeDatum & { fx?: number; fy?: number };
    this.centerNode.data.data.sprite = _.find(
      this.artworkSprites,
      (artworkSprite) => artworkSprite.isCentered,
    );
  }

  /**
   * Creates simulation forces with configurable strength
   * @param nodes Nodes for the simulation
   * @param links Links for the simulation
   * @returns D3 force simulation
   */
  private createSimulationForces(nodes: any[], links: any[]) {
    return forceSimulation(nodes)
      .force(
        "link",
        forceLink(links)
          .id((d: any) => d.uuid)
          .distance((d: any) => {
            if (d.source.parent === null) {
              // If directly connected to center node, use a fixed distance
              return this.dimensions.width * 0.5;
            } else {
              // Position as close as possible to parent node
              return 0;
            }
          })
          .strength(0.7),
      )
      .force("charge", forceManyBodyReuse().strength(-100))
      .force(
        "collision",
        forceCollide()
          .radius((d: any) => {
            if (d.depth === 1) return 0;
            const scaledRadius =
              d.data.data.sprite.radius * d.data.data.sprite.scaleFactor;
            return scaledRadius + this.nodePadding;
          })
          .strength(0.7),
      )
      .velocityDecay(0.4)
      .alphaMin(0.001)
      .alphaDecay(0.02)
      .alphaTarget(0);
  }

  public run(): void {
    console.info("Starting simulation...");
    console.time("Simulation Time");

    const simulation = this.createSimulationForces(this.nodes, this.links);

    simulation.on("tick", () => {
      this.keepRootNodeInCenter();
    });

    simulation.on("end", () => {
      this.keepRootNodeInCenter();

      // Store simulation results
      this.simulationResults = {
        nodes: this.nodes,
        links: this.links,
        root: this.root,
      };

      // Record end time and log duration
      console.timeEnd("Simulation Time");

      this.instance = simulation;
      // Notify callback
      if (this.onSimulationEnd) {
        this.onSimulationEnd(this.root, this.links, this.nodes);
      }
    });
    console.info("Simulation running...");
  }

  /**
   * Resets the simulation with animation
   * @param animationDuration Duration of the reset animation in milliseconds
   */
  /**
   * Updates sprite positions based on current node positions
   */
  private updateSprites(): void {
    this.nodes.forEach((node: any) => {
      if (node.data.data.sprite) {
        const sprite = node.data.data.sprite;
        sprite.x = node.x;
        sprite.y = node.y;
      }
    });
  }

  public resetSimulation(events: {
    onTick: () => void;
    onTickEnd: () => void;
  }): void {
    // If no previous simulation results, run the initial simulation
    if (!this.simulationResults) {
      return;
    }

    this.instance.stop();

    this.simulationResults.nodes.forEach((node) => {
      if (node.data.data.sprite) {
        node.data.data.sprite.visible = true;
      }
    });

    // Restore previous simulation state
    this.nodes = this.simulationResults.nodes;
    this.links = this.simulationResults.links;
    this.root = this.simulationResults.root;

    // Recreate simulation with animation
    const simulation = this.createSimulationForces(this.nodes, this.links);

    // Add tick event to update sprites in real-time
    simulation.on("tick", () => {
      this.keepRootNodeInCenter();
      this.updateSprites();
      if (events.onTick) events.onTick();
    });

    // Add end event to ensure final sprite positions are set
    simulation.on("end", () => {
      this.keepRootNodeInCenter();
      if (events.onTickEnd) events.onTickEnd();
      this.updateSprites();
    });

    this.instance = simulation;
  }

  /**
   * Filters nodes based on categories in real-time
   * @param categories Array of categories to keep
   * @param animationDuration Duration of the filtering animation in milliseconds
   */
  public filterNodesByCategories(categories: string[]): void {
    if (!this.simulationResults) {
      console.warn("No previous simulation results to filter");
      return;
    }

    this.instance.stop();

    // Filter nodes based on categories
    const filteredNodes = this.simulationResults.nodes.filter((node) => {
      // Skip the center node
      if (node.depth === 0) return true;

      // Check if node's categories match any of the provided categories
      const nodeCategories = node.data.data?.categories || [];
      return nodeCategories.some((category: string) =>
        categories.includes(category),
      );
    });

    // Hide sprites for nodes not in the filtered set
    this.simulationResults.nodes.forEach((node) => {
      if (node.data.data.sprite) {
        node.data.data.sprite.visible = filteredNodes.includes(node);
      }
    });

    // Recreate links based on filtered nodes
    const filteredLinks = this.simulationResults.links.filter(
      (link) =>
        filteredNodes.includes(link.source) &&
        filteredNodes.includes(link.target),
    );

    // Update current state
    this.nodes = filteredNodes;
    this.links = filteredLinks;

    // Create new simulation with filtered nodes
    const simulation = forceSimulation(this.nodes)
      .force(
        "link",
        forceLink(this.links)
          .id((d: any) => d.uuid)
          .distance((d: any) => {
            return 200;
          }),
      )
      .force(
        "radial",
        forceRadial(
          // Dynamically set spread radius based on number of nodes
          (d: any) => {
            // Calculate base radius with consideration for number of nodes
            const baseRadius =
              Math.min(this.dimensions.width, this.dimensions.height) * 0.4;

            // Spread nodes based on their depth and a logarithmic scaling
            return baseRadius * (1 + Math.log(d.depth + 1) * 0.5);
          },
          this.dimensions.width / 2, // X center
          this.dimensions.height / 2, // Y center
        ).strength(0.7),
      )
      .force(
        "collision",
        forceCollide()
          .radius((d: any) => {
            if (d.depth === 1) return 0;
            const scaledRadius =
              d.data.data.sprite.radius * d.data.data.sprite.scaleFactor;
            return scaledRadius + this.nodePadding;
          })
          .strength(0.7),
      )
      .force("charge", forceManyBody().strength(-50)) // Gentle repulsion
      .velocityDecay(0.3)
      .alphaMin(0.001)
      .alphaDecay(0.02)
      .alphaTarget(0);

    // Add tick event to update sprites in real-time
    simulation.on("tick", () => {
      this.updateSprites();
    });

    // Add end event to ensure final sprite positions are set
    simulation.on("end", () => {
      this.updateSprites();
      this.keepRootNodeInCenter();
    });

    this.instance = simulation;
  }

  private generateParentNodes(): any[] {
    const sortedTags = [...this.centeredArtwork.tags].sort();
    const tagCombinations = this.generateCombinations(sortedTags);

    const uniqueSetNames = [
      ...new Set(tagCombinations.map((combo) => combo.join(","))),
    ].sort();

    const setsData: SetData[] = uniqueSetNames
      .map((setName) => ({
        setName,
        artworks: this.artworks.filter((art) =>
          this.generateCombinations(art.tags)
            .map((combo) => combo.join(","))
            .includes(setName),
        ),
      }))
      .filter((set) =>
        set.artworks.some(
          (artwork) => artwork.uuid === this.centeredArtwork.uuid,
        ),
      );

    const artworkNodes = this.createArtworkNodes(setsData);

    const filteredArtworks = this.filterUniqueArtworksByMaxSetLength(
      artworkNodes,
    ).sort((a: any, b: any) =>
      a.name
        .toString()
        .toLowerCase()
        .localeCompare(b.name.toString().toLowerCase()),
    );

    const uniqueParents = [
      ...new Set(filteredArtworks.map((art) => art.parentId)),
    ].sort();

    const parentNodes = uniqueParents.map((parent) => ({
      name: parent,
      uuid: parent,
      parentId: this.centeredArtwork.uuid,
    }));

    return [
      {
        name: this.centeredArtwork.uuid,
        uuid: this.centeredArtwork.uuid,
        parentId: null,
      },
      ...parentNodes,
      ...filteredArtworks,
    ];
  }

  private generateCombinations(items: string[]): string[][] {
    return items
      .reduce(
        (acc: string[][], current: string) =>
          acc.concat(acc.map((comb) => [...comb, current])),
        [[]],
      )
      .slice(1);
  }

  private createArtworkNodes(sets: SetData[]): ArtworkNode[] {
    return sets
      .flatMap((set) =>
        set.artworks.map((artwork) => {
          const sprite = _.find(
            this.artworkSprites,
            (artworkSprite) => artworkSprite.label === artwork.uuid,
          );
          return {
            name: artwork.name,
            uuid: artwork.uuid,
            parentId: set.setName,
            sprite: sprite,
            categories: artwork.tags,
          };
        }),
      )
      .filter((art) => art.uuid !== this.centeredArtwork.uuid);
  }

  private filterUniqueArtworksByMaxSetLength(
    artworks: ArtworkNode[],
  ): ArtworkNode[] {
    return artworks.filter((art, index, array) => {
      const sameIdArts = array.filter((a) => a.uuid === art.uuid);
      if (sameIdArts.length === 1) return true;

      const currentSetLength = art.parentId!.split(",").length;
      const maxSetLength = Math.max(
        ...sameIdArts.map((a) => a.parentId!.split(",").length),
      );
      return maxSetLength === currentSetLength;
    });
  }

  private keepRootNodeInCenter(): void {
    if (this.centerNode) {
      this.centerNode.fx = this.dimensions.width / 2;
      this.centerNode.fy = this.dimensions.height / 2;
    }
  }
}
